import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Sparkles,
    Plus,
    Wrench,
    Bug,
    ShieldCheck,
    Tag,
    Calendar,
    GitCommit,
} from "lucide-react";
import { fetchChangelog } from "../lib/cmsApi";

const KIND = {
    new: {
        icon: Plus,
        label: "New",
        accent: "text-emerald-300",
        chip: "bg-emerald-300/10 border-emerald-300/25",
    },
    improved: {
        icon: Wrench,
        label: "Improved",
        accent: "text-cyan-300",
        chip: "bg-cyan-300/10 border-cyan-300/25",
    },
    fixed: {
        icon: Bug,
        label: "Fixed",
        accent: "text-amber-glow",
        chip: "bg-amber-300/10 border-amber-300/25",
    },
    security: {
        icon: ShieldCheck,
        label: "Security",
        accent: "text-rose-300",
        chip: "bg-rose-300/10 border-rose-300/25",
    },
};

const FALLBACK_RELEASES = [
    {
    v: "3.0.0",
    date: "Jun 15, 2026",
    title: "ApexBrain Intelligence Layer",
    highlight: true,
    notes: [
    { k: "new", t: "Cross-functional AI Intelligence Layer launched." },
    { k: "new", t: "Executive Briefings generated across Sales, HR, Finance and Projects." },
    { k: "new", t: "Business Health Score for organization-wide visibility." },
    { k: "improved", t: "Real-time analytics engine redesigned for faster insights." },
    { k: "security", t: "Advanced audit trail coverage across all modules." }
    ]
    },
    
    {
    v: "2.9.0",
    date: "May 28, 2026",
    title: "Leadership Command Center",
    notes: [
    { k: "new", t: "Leadership dashboard with company-wide KPIs." },
    { k: "new", t: "Department performance benchmarking." },
    { k: "improved", t: "Executive reports now support scheduled delivery." },
    { k: "fixed", t: "Resolved dashboard filter inconsistencies." }
    ]
    },
    
    {
    v: "2.8.0",
    date: "May 12, 2026",
    title: "Documents & Knowledge Hub",
    notes: [
    { k: "new", t: "Centralized Knowledge Base." },
    { k: "new", t: "Collaborative Wiki and Whiteboards." },
    { k: "new", t: "Version history for business documents." },
    { k: "improved", t: "Global search now indexes documents and knowledge articles." },
    { k: "security", t: "Document-level access controls added." }
    ]
    },
    
    {
    v: "2.7.0",
    date: "Apr 28, 2026",
    title: "Communications Workspace",
    notes: [
    { k: "new", t: "Unified team communication hub." },
    { k: "new", t: "Department channels and announcements." },
    { k: "new", t: "Context-aware notifications." },
    { k: "fixed", t: "Improved message delivery reliability." }
    ]
    },
    
    {
    v: "2.6.0",
    date: "Apr 10, 2026",
    title: "Business Intelligence Layer",
    notes: [
    { k: "new", t: "Growth Bottleneck Finder." },
    { k: "new", t: "AI-generated business insights." },
    { k: "new", t: "Cross-department analytics dashboard." },
    { k: "improved", t: "Executive visibility across all business functions." },
    { k: "security", t: "Insight access controls for leadership roles." }
    ]
    },
    
    {
    v: "2.5.0",
    date: "Mar 22, 2026",
    title: "Advanced Forecasting",
    notes: [
    { k: "new", t: "Revenue forecasting engine." },
    { k: "new", t: "Cashflow prediction models." },
    { k: "new", t: "Project delivery forecasting." },
    { k: "improved", t: "Forecast accuracy enhanced using historical trends." }
    ]
    },
    
    {
    v: "2.4.0",
    date: "Mar 05, 2026",
    title: "Workload & Capacity Planning",
    notes: [
    { k: "new", t: "Team workload balancing." },
    { k: "new", t: "Resource capacity planning." },
    { k: "new", t: "Project utilization reports." },
    { k: "fixed", t: "Resolved scheduling conflicts in shared resources." }
    ]
    },
    
    {
    v: "2.3.0",
    date: "Feb 18, 2026",
    title: "Finance & Accounting Suite",
    notes: [
    { k: "new", t: "Cashflow Management." },
    { k: "new", t: "Profit & Loss reporting." },
    { k: "new", t: "Balance Sheets and Chart of Accounts." },
    { k: "new", t: "Invoice and Ledger Management." },
    { k: "security", t: "Financial audit logs enabled." }
    ]
    },
    
    {
    v: "2.2.0",
    date: "Feb 01, 2026",
    title: "People Operations",
    notes: [
    { k: "new", t: "Payroll processing." },
    { k: "new", t: "Attendance and Leave Management." },
    { k: "new", t: "Employee onboarding workflows." },
    { k: "new", t: "Intern management." },
    { k: "improved", t: "Shift scheduling optimization." }
    ]
    },
    
    {
    v: "2.1.0",
    date: "Jan 18, 2026",
    title: "Projects & Delivery",
    notes: [
    { k: "new", t: "Gantt Charts and Project Scheduling." },
    { k: "new", t: "Timesheets and Task Tracking." },
    { k: "new", t: "Project Health Monitoring." },
    { k: "improved", t: "Task performance across large projects improved." }
    ]
    },
    
    {
    v: "2.0.0",
    date: "Jan 01, 2026",
    title: "Unified Business Platform",
    highlight: true,
    notes: [
    { k: "new", t: "Single workspace for Sales, HR, Projects and Finance." },
    { k: "new", t: "Unified navigation and dashboards." },
    { k: "new", t: "Organization-wide reporting engine." },
    { k: "security", t: "Role-based access controls across all modules." }
    ]
    },
    
    {
    v: "1.5.0",
    date: "Dec 15, 2025",
    title: "Sales Intelligence",
    notes: [
    { k: "new", t: "Sales Intelligence dashboard." },
    { k: "new", t: "Pipeline visibility and activity tracking." },
    { k: "new", t: "Customer lifecycle management." },
    { k: "improved", t: "Custom sales reports and exports." }
    ]
    },
    
    {
    v: "1.3.0",
    date: "Nov 28, 2025",
    title: "CRM Foundation",
    notes: [
    { k: "new", t: "Lead Management." },
    { k: "new", t: "Customer Management." },
    { k: "new", t: "Quotation workflows." },
    { k: "new", t: "Billing and revenue tracking." }
    ]
    },
    
    {
    v: "1.1.0",
    date: "Nov 12, 2025",
    title: "Team Collaboration",
    notes: [
    { k: "new", t: "Tasks and team workspaces." },
    { k: "new", t: "Shared calendars." },
    { k: "new", t: "Activity tracking." },
    { k: "fixed", t: "Improved notification reliability." }
    ]
    },
    
    {
    v: "1.0.0",
    date: "Nov 01, 2025",
    title: "ApexBrain Public Launch",
    highlight: true,
    notes: [
    { k: "new", t: "Unified business dashboard." },
    { k: "new", t: "Document management and file repository." },
    { k: "new", t: "Basic reporting and analytics." },
    { k: "security", t: "Encrypted storage and secure backups." }
    ]
    }
    ];
    

const FILTERS = [
    { id: "all", label: "All" },
    { id: "new", label: "New" },
    { id: "improved", label: "Improved" },
    { id: "fixed", label: "Fixed" },
    { id: "security", label: "Security" },
];

export default function ChangelogPage() {
    const [filter, setFilter] = useState("all");
    const [allReleases, setAllReleases] = useState(FALLBACK_RELEASES);

    useEffect(() => {
        fetchChangelog()
            .then((data) => {
                if (data?.releases?.length) setAllReleases(data.releases);
            })
            .catch(() => {});
    }, []);

    const releases = useMemo(() => {
        if (filter === "all") return allReleases;
        return allReleases.map((r) => ({
            ...r,
            notes: r.notes.filter((n) => n.k === filter),
        })).filter((r) => r.notes.length > 0);
    }, [filter, allReleases]);

    return (
        <div className="pt-28 lg:pt-32" data-testid="changelog-page">
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // changelog · what shipped, week by week
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="changelog-title"
                        >
                            Every release.
                            <br />
                            <span className="gradient-text">
                                In the open.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        We ship every week. New surfaces, sharper edges, faster
                        graphs — and the occasional bug we&apos;d rather you
                        didn&apos;t notice.
                    </p>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 mt-10 flex flex-wrap gap-2" data-testid="changelog-filters">
                    {FILTERS.map((f) => {
                        const a = f.id === "all" ? null : KIND[f.id];
                        const active = filter === f.id;
                        return (
                            <button
                                key={f.id}
                                onClick={() => setFilter(f.id)}
                                data-testid={`changelog-filter-${f.id}`}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                                    active
                                        ? a
                                            ? `${a.chip} text-white`
                                            : "bg-white/[0.08] border-white/20 text-white"
                                        : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
                                }`}
                            >
                                <span
                                    className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-400 animate-pulse-glow" : "bg-zinc-600"}`}
                                />
                                {f.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            <section className="relative pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <aside className="lg:col-span-3">
                        <div className="lg:sticky lg:top-28 rounded-2xl border border-white/[0.06] bg-obsidian-200/60 p-5">
                            <p className="font-code text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-4">
                                // versions
                            </p>
                            <ul className="space-y-2.5">
                                {allReleases.map((r) => (
                                    <li key={r.v}>
                                        <a
                                            href={`#v${r.v}`}
                                            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition"
                                        >
                                            <Tag className="w-3 h-3 text-electric" />
                                            <span className="font-code">
                                                v{r.v}
                                            </span>
                                            <span className="ml-auto text-[11px] text-zinc-600">
                                                {r.date.split(",")[0]}
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    {/* Timeline */}
                    <div className="lg:col-span-9 relative">
                        <div className="absolute left-3 top-0 bottom-0 w-px bg-white/[0.08]" aria-hidden />
                        <div className="space-y-10">
                            {releases.map((r, i) => (
                                <motion.article
                                    key={r.v}
                                    id={`v${r.v}`}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    className="relative pl-12"
                                    data-testid={`changelog-release-${r.v}`}
                                >
                                    <span className={`absolute left-0 top-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full border ${r.highlight ? "bg-electric/15 border-electric/40 text-electric shadow-[0_0_18px_rgba(59,130,246,0.35)]" : "bg-obsidian-200 border-white/[0.08] text-zinc-400"}`}>
                                        <GitCommit className="w-3.5 h-3.5" />
                                    </span>
                                    <div className="flex flex-wrap items-baseline gap-3 mb-2">
                                        <h2 className="font-outfit text-xl sm:text-2xl font-semibold text-white">
                                            v{r.v}
                                            {r.highlight && (
                                                <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-electric/10 border border-electric/30 px-2 py-0.5 font-code text-[10px] text-electric uppercase tracking-[0.18em]">
                                                    <Sparkles className="w-2.5 h-2.5" />
                                                    highlight
                                                </span>
                                            )}
                                        </h2>
                                        <span className="font-code text-[11px] text-zinc-500 inline-flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {r.date}
                                        </span>
                                    </div>
                                    <p className="text-zinc-300 mb-5">
                                        {r.title}
                                    </p>
                                    <ul className="space-y-2">
                                        {r.notes.map((n, j) => {
                                            const K = KIND[n.k];
                                            return (
                                                <li
                                                    key={j}
                                                    className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
                                                    data-testid={`changelog-note-${r.v}-${j}`}
                                                >
                                                    <span
                                                        className={`shrink-0 inline-flex items-center gap-1 rounded-full border ${K.chip} px-2 py-0.5`}
                                                    >
                                                        <K.icon
                                                            className={`w-3 h-3 ${K.accent}`}
                                                        />
                                                        <span
                                                            className={`text-[10px] font-code uppercase tracking-[0.18em] font-semibold ${K.accent}`}
                                                        >
                                                            {K.label}
                                                        </span>
                                                    </span>
                                                    <p className="text-sm text-zinc-200 leading-relaxed">
                                                        {n.t}
                                                    </p>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
